import { Form, Formik, FormikErrors } from "formik";
import { useState } from "react";
import * as yup from "yup";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Tooltip
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import FormTextField from "src/components/form/FormTextField";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { VanIntegrationFields } from "src/graphql/generated/VanIntegrationFields";
import { getErrorMessage } from "src/util/errors";

import IntegrationTypeSelect from "./IntegrationTypeSelect";

type FormValues = {
  name: string;
  password: string;
};

type FormValuesAndErrors = FormValues & {
  saveErrors?: any;
};

const useStyles = makeStyles(theme => ({
  saveError: {
    marginBottom: theme.spacing(3)
  }
}));

export default function EditIntegrationDialog({
  open,
  onClose,
  integration,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  integration: VanIntegrationFields;
  onSubmit: (values: { name: string; password: string }) => Promise<void>;
}): JSX.Element {
  const [saveErrors, setSaveErrors] = useState<string | null>(null);

  const handleSubmit = async (payload: FormValues) => {
    setSaveErrors(null);

    try {
      await onSubmit(payload);
    } catch (e) {
      setSaveErrors(getErrorMessage(e));
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle>Edit Integration</DialogTitle>
      <Formik
        initialValues={{ name: integration.name, password: "" }}
        validationSchema={yup.object().shape({
          name: yup.string().required("Required"),
          password: yup.string()
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <EditIntegrationForm
            isSubmitting={isSubmitting}
            onClose={onClose}
            integration={integration}
            saveErrors={saveErrors}
          />
        )}
      </Formik>
    </Dialog>
  );
}

function EditIntegrationForm({
  isSubmitting,
  onClose,
  integration,
  saveErrors
}: {
  isSubmitting?: boolean;
  onClose: () => void;
  integration: VanIntegrationFields;
  saveErrors: string | null;
}) {
  const classes = useStyles();

  return (
    <Form>
      <DialogContent>
        {/*
        Note that we have two error banners here -- the <Alert> displays server-side
        errors (like errors validating the API key with VAN), and the <FormikErrorBanner>
        is our standard Formik banner that displays client-side validation errors from Formik.
        */}
        {saveErrors && (
          <Alert severity="error" className={classes.saveError}>
            {saveErrors}
          </Alert>
        )}
        <FormikErrorBanner
          renderBanner={(errors: FormikErrors<FormValuesAndErrors>) => {
            if (errors.saveErrors) {
              return `Error saving integration: ${errors.saveErrors}`;
            }
            return "Please fix the errors indicated below.";
          }}
        />
        <IntegrationTypeSelect />
        <FormikTextField fullWidth name="name" label="Name this integration" />
        <Tooltip
          title="To use a different application name, please create a new integration."
          placement="bottom-start"
        >
          <span>
            <FormTextField
              fullWidth
              disabled
              label="Application name"
              value={integration.applicationName}
            />
          </span>
        </Tooltip>
        <FormikTextField
          fullWidth
          name="password"
          label="API key (leave blank to keep current)"
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={isSubmitting} onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          color="primary"
          variant="contained"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Form>
  );
}
